import moment from 'moment';
import { ColorState, TransferReservationBaseFiltersFormType } from '../../viajeTraslado/components/ViajeTrasladoBaseFilters';

export interface OpenedTravelColumnVisibility {
  schedule: boolean;
  cliente: boolean;
  puntos: boolean;
  coordinationObservation: boolean;
  general_observation: boolean;
  serviceType: boolean;
  event_name: boolean;
  id: boolean;
  platformId: boolean;
  assigned_driver: boolean;
  passengers: boolean;
  contretemps: boolean;
  state: boolean;
  actions: boolean;
}

export const openedTravelColumnLabels: Record<
  keyof OpenedTravelColumnVisibility,
  string
> = {
  schedule: 'Horarios',
  cliente: 'Cliente',
  puntos: 'Domicilios',
  coordinationObservation: 'Obs. Coordinación',
  general_observation: 'Obs. Chofer',
  serviceType: 'Servicio',
  event_name: 'Evento',
  id: 'ID',
  platformId: 'Plataforma',
  assigned_driver: 'Chofer',
  passengers: 'Pasajeros',
  contretemps: 'Contratiempos',
  state: 'Estado',
  actions: 'Acciones'
};

export interface SortState {
  sortDir: string;
  sortBy: string;
}

export interface OpenedTravelState {
  filters: TransferReservationBaseFiltersFormType;
  page: number;
  rowsPerPage: number;
  sort: SortState;
  columnVisibility: OpenedTravelColumnVisibility;
  lastAccessDate?: string; // Fecha del último acceso para detectar cambios de día
}

const STORAGE_KEY = 'openedTravelState';

export const getDefaultFilters =
  (): TransferReservationBaseFiltersFormType => ({
    dateFrom: null,
    dateTo: null,
    clientId: null,
    serviceType: 'Todos',
    hasPriority: false,
    passangerName: '',
    client_type: 'AllClient',
    street: '',
    locality: '',
    phone: '',
    client_phone: '',
    reservationId: '',
    state: 'Todos',
    assigned_driver_id: null,
    driverCode: '',
    oficial_cuenta_id: null,
    authorized_id: null,
    cost_center: '',
    buy_order: '',
    sector: '',
    driver_with_close_shift: false,
    departure_time_exceeded: false,
    sent_reservations_platform: false,
    not_sent_reservations_platform: false,
    has_platform_id: false,
    is_event: false,
    is_llt_pais: false,
    event_name: '',
    bus_type_id: [],
    color: undefined
  });

export const getDefaultColumnVisibility = (): OpenedTravelColumnVisibility => ({
  schedule: true,
  cliente: true,
  puntos: true,
  coordinationObservation: true,
  general_observation: true,
  serviceType: true,
  event_name: true,
  id: true,
  platformId: true,
  assigned_driver: true,
  passengers: true,
  contretemps: true,
  state: true,
  actions: true
});

export const getDefaultSort = (): SortState => ({
  sortDir: 'ASC',
  sortBy: 'created_at'
});

const getDefaultState = (): OpenedTravelState => ({
  filters: getDefaultFilters(),
  page: 1,
  rowsPerPage: 100,
  sort: getDefaultSort(),
  columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
  storedState: Partial<OpenedTravelState>
): OpenedTravelState => {
  const defaultFilters = getDefaultFilters();
  const today = moment().format('YYYY-MM-DD');
  const lastAccessDate = storedState.lastAccessDate;
  const isNewDay = !lastAccessDate || lastAccessDate !== today;

  return {
    ...getDefaultState(),
    ...storedState,
    filters: {
      ...defaultFilters,
      ...(storedState.filters || {}),
      dateFrom: isNewDay
        ? null
        : storedState.filters?.dateFrom
        ? moment(storedState.filters.dateFrom)
        : null,
      dateTo: isNewDay
        ? null
        : storedState.filters?.dateTo
        ? moment(storedState.filters.dateTo)
        : null
    },
    lastAccessDate: today
  };
};

export const loadStateFromLocalStorage = (): OpenedTravelState => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return deserializeState(parsed);
    }
  } catch (error) {
    console.error('Error loading opened travel state:', error);
  }
  return getDefaultState();
};

export const saveStateToLocalStorage = (state: OpenedTravelState): void => {
  try {
    const stateWithDate = {
      ...state,
      lastAccessDate: moment().format('YYYY-MM-DD')
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
  } catch (error) {
    console.error('Error saving opened travel state:', error);
  }
};

export const clearStateFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing opened travel state:', error);
  }
};

export const clearStateAndAddFilterColorState = (
  filter:
    | { color: ColorState | undefined }
    | { reservationId: number }
): void => {
  try {
    clearStateFromLocalStorage();
    const state = getDefaultState();
    if ('color' in filter) {
      state.filters.color = filter.color;
    } else {
      state.filters.reservationId = filter.reservationId.toString();
    }
    saveStateToLocalStorage(state);
  } catch (error) {
    console.error('Error clearing opened travel state:', error);
  }
};
